import { Component } from '@theme/component';
import { morph } from '@theme/morph';

class MobileBuyBarComponent extends Component {
  requiredRefs = ['bar'];

  /** @type {string | null} */
  #pendingAction = null;

  /** @type {boolean} */
  #pendingBuyNow = false;

  /** @type {boolean} */
  #userChose = false;

  /** @type {HTMLElement | null} */
  #pk = null;

  /** @type {EventListener | null} */
  #variantChangeHandler = null;

  /** @type {EventListener} */
  #sizeSelectHandler = (event) => {
    this.handleSizeSelect(event);
  };

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {Function | null} */
  #originalReplaceState = null;

  /** Milliseconds without a scroll event before the bar hides again. */
  #scrollIdleDelay = 1500;

  /** Scroll offset (px) treated as "at the top", allowing for rubber-banding. */
  #topThreshold = 8;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #scrollIdleTimeout = null;

  /** Matches the breakpoint the bar's CSS uses; desktop is never touched. */
  #mobileQuery = window.matchMedia('(max-width: 767px)');

  /**
   * Reveals the bar as soon as the page scrolls in either direction, and
   * re-arms the idle timer so it stays up while scrolling continues.
   * @type {EventListener}
   */
  #onScroll = () => {
    if (!this.#mobileQuery.matches) return;

    // Back at the top of the page: hide straight away instead of waiting out
    // the idle timer. The drawer guard still applies, since the drawer locks
    // scrolling and hiding here would strand it.
    if (window.scrollY <= this.#topThreshold && !this.classList.contains('is-drawer-open')) {
      if (this.#scrollIdleTimeout) {
        clearTimeout(this.#scrollIdleTimeout);
        this.#scrollIdleTimeout = null;
      }
      this.classList.remove('is-scrolling');
      return;
    }

    this.classList.add('is-scrolling');

    if (this.#scrollIdleTimeout) clearTimeout(this.#scrollIdleTimeout);
    this.#scrollIdleTimeout = setTimeout(this.#onScrollIdle, this.#scrollIdleDelay);
  };

  /** Hides the bar once scrolling stops, unless the shopper is mid-interaction. */
  #onScrollIdle = () => {
    // Never pull the bar out from under an open drawer (which locks scrolling,
    // so no further scroll event would bring it back) or from under a tap or
    // keyboard focus in progress. Wait out another idle period instead.
    if (
      this.classList.contains('is-drawer-open') ||
      this.contains(document.activeElement) ||
      this.matches(':hover')
    ) {
      this.#scrollIdleTimeout = setTimeout(this.#onScrollIdle, this.#scrollIdleDelay);
      return;
    }

    this.#scrollIdleTimeout = null;
    this.classList.remove('is-scrolling');
  };

  connectedCallback() {
    super.connectedCallback();
    
    // Intercept history.replaceState to detect sibling navigation reliably
    if (!this.#originalReplaceState) {
      this.#originalReplaceState = history.replaceState.bind(history);
      history.replaceState = (state, title, url) => {
        this.#originalReplaceState(state, title, url);
        
        const newHandle = url?.toString().split('/products/')?.[1]?.split('?')?.[0];
        const currentHandle = window.location.pathname.split('/products/')?.[1]?.split('?')?.[0];
        
        if (newHandle && newHandle !== currentHandle) {
          // URL changed to a different product — sibling navigation occurred
          setTimeout(() => {
            this.#reinitForSibling();
          }, 100);
        }
      };
    }

    this.#setupEventListeners();

    this.classList.add('is-ready');
    document.body.classList.add('eagleray-msab-active');
    
    this.#cacheInternalElements();
    this.#deselectWithRetry(20);
    this.#updateBar();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#originalReplaceState) {
      history.replaceState = this.#originalReplaceState;
      this.#originalReplaceState = null;
    }
    if (this.#scrollIdleTimeout) {
      clearTimeout(this.#scrollIdleTimeout);
      this.#scrollIdleTimeout = null;
    }
    this.#abortController.abort();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#updateBar();
  }

  #setupEventListeners() {
    const { signal } = this.#abortController;

    // Listen to variant updates anywhere on the document (bubbling up from sibling sections)
    document.addEventListener('variant:update', this.#onVariantUpdate, { signal });
    
    // Listen to native picker changes
    this.#pk = document.querySelector(`variant-picker[data-product-id="${this.dataset.productId}"]`);
    this.#variantChangeHandler = () => {
      this.#userChose = true;
      this.#updateBar();
    };
    if (this.#pk) {
      this.#pk.addEventListener('change', this.#variantChangeHandler, { signal });
    }

    // Reveal the bar while the page is scrolling (see .is-scrolling in eagleray.css)
    window.addEventListener('scroll', this.#onScroll, { passive: true, signal });

    // Cart events
    document.addEventListener('cart:update', this.#onCartUpdate, { signal });
    document.addEventListener('cart:error', this.#onCartError, { signal });

    // Esc to close drawer
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.classList.contains('is-drawer-open')) {
        this.closeDrawer();
      }
    }, { signal });
  }

  #reinitForSibling() {
    // 1. Remove the stale `change` listener
    if (this.#pk && this.#variantChangeHandler) {
      this.#pk.removeEventListener('change', this.#variantChangeHandler);
    }

    // 2. Re-query the new <variant-picker> element
    this.#pk = this.closest('[id^="MainProduct"]')?.querySelector('variant-picker') ?? null;
    if (!this.#pk) {
      // Fallback if not inside MainProduct
      this.#pk = document.querySelector(`variant-picker[data-product-id="${this.dataset.productId}"]`);
    }

    // 3. Re-bind the change listener to the new variant-picker
    if (this.#pk && this.#variantChangeHandler) {
      this.#pk.addEventListener('change', this.#variantChangeHandler);
    }

    // 4. Re-query any internal child element references
    //    Yield to the base class MutationObserver first, THEN reset UI
    requestAnimationFrame(() => {
      this.#cacheInternalElements();

      // 5. Reset UI state for the new product
      this.#deselectWithRetry(20);
      this.#updateBar();
    });
  }

  #cacheInternalElements() {
    // 1. Re-query all refs
    this.refs.bar = this.querySelector('.eagleray-msab__bar');
    this.refs.sizeTextEl = this.querySelector('.eagleray-msab__size-text');
    this.refs.changeLabelEl = this.querySelector('.eagleray-msab__change');
    this.refs.atcBtn = this.querySelector('.eagleray-msab__atc');
    this.refs.atcTextEl = this.querySelector('.eagleray-msab__btn-label');
    this.refs.buyNowBtn = this.querySelector('.eagleray-msab__buynow');
    this.refs.overlay = this.querySelector('.eagleray-msab__overlay');
    this.refs.drawer = this.querySelector('.eagleray-msab__drawer');
    this.refs.sizeOptions = [...this.querySelectorAll('.eagleray-msab__size-option')];

    // 2. Create bound handlers once
    this._openDrawer = this._openDrawer || this.openDrawer.bind(this);
    this._closeDrawer = this._closeDrawer || this.closeDrawer.bind(this);
    this._handleSizeChart = this._handleSizeChart || this.handleSizeChartClick.bind(this);
    this._handleAtc = this._handleAtc || this.handleAtcClick.bind(this);
    this._handleBuyNow = this._handleBuyNow || this.handleBuyNowClick.bind(this);

    // 3. Helper to safely bind click
    const bindClick = (el, handler) => {
      if (!el) return;
      el.removeEventListener('click', handler);
      el.addEventListener('click', handler);
    };

    // 4. Bind all interactions manually since morph detaches declarative on:click listeners
    const sizeTrigger = this.querySelector('.eagleray-msab__size');
    bindClick(sizeTrigger, this._openDrawer);
    bindClick(this.refs.changeLabelEl, this._openDrawer);
    
    bindClick(this.refs.overlay, this._closeDrawer);
    bindClick(this.querySelector('.eagleray-msab__drawer-handle'), this._closeDrawer);
    bindClick(this.querySelector('.eagleray-msab__drawer-close'), this._closeDrawer);

    bindClick(this.querySelector('.eagleray-msab__sizechart'), this._handleSizeChart);
    bindClick(this.querySelector('.eagleray-msab__drawer-sizechart'), this._handleSizeChart);

    bindClick(this.refs.atcBtn, this._handleAtc);
    bindClick(this.refs.buyNowBtn, this._handleBuyNow);

    this.refs.sizeOptions.forEach(btn => bindClick(btn, this.#sizeSelectHandler));
  }

  get #productId() {
    return this.dataset.productId;
  }

  get #hasSize() {
    return this.dataset.hasSize === 'true';
  }

  get #selectedSuffix() {
    return this.dataset.selectedSuffix || 'Selected';
  }

  #picker() {
    return document.querySelector(`variant-picker[data-product-id="${this.#productId}"]`);
  }

  #productForm() {
    const mainForm = document.querySelector('form[id^="BuyButtons-ProductForm-"]');
    if (mainForm) return mainForm.closest('product-form-component') || mainForm;
    return document.querySelector(
      `[id^="MainProduct"] product-form-component[data-product-id="${this.#productId}"], ` +
      `product-form-component:not([id*="QuickAdd"])[data-product-id="${this.#productId}"]`
    );
  }

  #sizeFieldset() {
    const pk = this.#picker();
    if (!pk) return null;
    const legend = pk.querySelector('.variant-option__legend--size, .variant-option__label--size');
    return legend ? legend.closest('fieldset, .variant-option--dropdowns') : null;
  }

  #sizeRadios() {
    const fs = this.#sizeFieldset();
    return fs ? Array.from(fs.querySelectorAll('input[type="radio"]')) : [];
  }

  #checkedRadio() {
    return this.#sizeRadios().find(r => r.checked) || null;
  }

  #realAtcButton() {
    const pf = this.#productForm();
    return pf ? (pf.querySelector('[name="add"]') || pf.querySelector('[type="submit"]') || pf.querySelector('.eagleray-upsell__add-btn')) : null;
  }

  // ---- public action handlers ----

  openDrawer(event) {
    if (event) event.preventDefault();
    if (!this.refs.drawer) return;
    
    if (this.refs.overlay) this.refs.overlay.removeAttribute('hidden');
    this.refs.drawer.removeAttribute('hidden');
    
    // force reflow
    void this.refs.drawer.offsetHeight;
    this.classList.add('is-drawer-open');
    document.body.style.overflow = 'hidden';
  }

  closeDrawer(event) {
    if (event) event.preventDefault();
    if (!this.refs.drawer) return;
    
    this.classList.remove('is-drawer-open');
    document.body.style.overflow = '';
    this.#pendingAction = null;
    
    const done = () => {
      if (this.classList.contains('is-drawer-open')) return;
      this.refs.drawer.setAttribute('hidden', '');
      if (this.refs.overlay) this.refs.overlay.setAttribute('hidden', '');
      this.refs.drawer.removeEventListener('transitionend', done);
    };
    
    this.refs.drawer.addEventListener('transitionend', done);
    setTimeout(done, 400); // fallback
  }

  handleAtcClick(event) {
    if (event) event.preventDefault();
    if (this.refs.atcBtn?.classList.contains('is-disabled')) return;
    this.#openDrawerForAction('atc');
  }

  handleBuyNowClick(event) {
    if (event) event.preventDefault();
    if (this.refs.buyNowBtn?.classList.contains('is-disabled')) return;
    this.#openDrawerForAction('buynow');
  }

  handleSizeChartClick(event) {
    if (event) event.preventDefault();
    if (typeof window.erScOpen === 'function') {
      window.erScOpen();
      return;
    }
    const native = document.querySelector('.er-sc-btn');
    if (native) native.click();
  }

  handleSizeSelect(event, data) {
    if (event) event.preventDefault();
    
    // Use currentTarget for manual addEventListener, fallback to target for proxy events
    const opt = event.currentTarget || event.target;
    if (!opt || opt.getAttribute('data-available') === 'false') return;
    
    const variantId = opt.getAttribute('data-variant-id');
    const value = opt.getAttribute('data-value');
    
    this.#chooseSize(variantId, value);
  }

  // ---- internal logic ----

  #openDrawerForAction(action) {
    if (!this.#hasSize || this.#checkedRadio()) {
      action === 'buynow' ? this.#triggerBuyNow() : this.#triggerAddToCart();
      return;
    }
    this.#pendingAction = action;
    this.openDrawer();
  }

  #chooseSize(variantId, value) {
    const radio = this.#findNativeRadio(variantId, value);
    this.#userChose = true;
    const action = this.#pendingAction;
    this.#pendingAction = null;

    let willUpdate = false;
    if (radio && !radio.checked) {
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
      willUpdate = true;
    }
    
    this.closeDrawer();

    if (action) {
      if (willUpdate) {
        this.#waitForVariantUpdate(() => {
          if (action === 'buynow') this.#triggerBuyNow();
          else this.#triggerAddToCart();
        });
      } else {
        if (action === 'buynow') this.#triggerBuyNow();
        else this.#triggerAddToCart();
      }
    }
    this.#updateBar();
  }

  #findNativeRadio(variantId, value) {
    const fs = this.#sizeFieldset();
    if (!fs) return null;
    
    let radio = null;
    if (variantId) {
      radio = fs.querySelector(`input[type="radio"][data-variant-id="${variantId}"]`);
    }
    if (!radio && value && window.CSS && CSS.escape) {
      radio = fs.querySelector(`input[type="radio"][value="${CSS.escape(value)}"]`);
    }
    if (!radio && value) {
      radio = this.#sizeRadios().find(r => r.value === value) || null;
    }
    return radio;
  }

  #waitForVariantUpdate(cb) {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      document.removeEventListener('variant:update', handler);
      cb();
    };
    
    const handler = (e) => {
      if (this.#isOurVariantEvent(e)) finish();
    };
    
    document.addEventListener('variant:update', handler);
    setTimeout(finish, 700); // fallback
  }

  #triggerAddToCart() {
    const btn = this.#realAtcButton();
    if (!btn || btn.disabled) return;
    btn.dataset.puppet = 'true';
    btn.click();
    this.#flashAdded();
  }

  #triggerBuyNow() {
    const pf = this.#productForm();
    const accel = pf?.querySelector('.shopify-payment-button button, [ref="acceleratedCheckoutButtonContainer"] button');
    
    if (accel && !accel.disabled) {
      accel.click();
      return;
    }
    
    this.#pendingBuyNow = true;
    this.#triggerAddToCart();
  }

  #flashAdded() {
    if (this.refs.atcBtn) {
      this.refs.atcBtn.classList.add('is-added');
    }
  }

  #updateBar() {
    const realBtn = this.#realAtcButton();
    const soldOut = !!(realBtn && realBtn.disabled);
    const chosen = this.#checkedRadio();

    if (this.#hasSize) {
      if (chosen) {
        if (this.refs.sizeTextEl) {
          this.refs.sizeTextEl.textContent = '';
          const token = document.createElement('b');
          token.textContent = chosen.value;
          this.refs.sizeTextEl.appendChild(token);
          this.refs.sizeTextEl.appendChild(document.createTextNode(' ' + this.#selectedSuffix));
        }
        if (this.refs.changeLabelEl) {
          this.refs.changeLabelEl.removeAttribute('hidden');
        }
        this.classList.add('has-size');
      } else {
        if (this.refs.sizeTextEl) {
          // Use default text if we have it, else fallback to generic string
          const defaultLabel = this.refs.sizeTextEl.dataset.defaultLabel || 'Select Size';
          this.refs.sizeTextEl.textContent = defaultLabel;
        }
        if (this.refs.changeLabelEl) {
          this.refs.changeLabelEl.setAttribute('hidden', '');
        }
        this.classList.remove('has-size');
      }
    }

    const blockPurchase = this.#hasSize && chosen && soldOut;
    
    if (this.refs.atcBtn) {
      this.refs.atcBtn.classList.toggle('is-disabled', blockPurchase);
      if (this.refs.atcTextEl) {
        const defaultAtcLabel = this.refs.atcTextEl.dataset.defaultLabel || 'Add to Bag';
        this.refs.atcTextEl.textContent = blockPurchase 
          ? (this.refs.atcBtn.getAttribute('data-soldout') || 'Sold Out')
          : defaultAtcLabel;
      }
    }
    
    if (this.refs.buyNowBtn) {
      this.refs.buyNowBtn.classList.toggle('is-disabled', blockPurchase);
    }

    // Update drawer options
    if (this.refs.drawer && this.refs.sizeOptions) {
      this.refs.sizeOptions.forEach((opt) => {
        const vid = opt.getAttribute('data-variant-id');
        opt.classList.toggle('is-selected', !!(chosen && vid && chosen.getAttribute('data-variant-id') === vid));
      });
    }
  }

  #applyNoPreselect() {
    if (!this.#hasSize || this.#userChose) return true;
    const radios = this.#sizeRadios();
    if (!radios.length) return false;
    
    radios.forEach(r => { if (r.checked) r.checked = false; });
    this.#updateBar();
    return true;
  }

  #deselectWithRetry(attempts) {
    if (this.#applyNoPreselect()) return;
    if (attempts <= 0) return;
    requestAnimationFrame(() => this.#deselectWithRetry(attempts - 1));
  }

  #isOurVariantEvent(e) {
    const pid = e?.detail?.data?.productId;
    return !pid || String(pid) === String(this.#productId);
  }

  #onVariantUpdate = (e) => {
    // Determine if it's a sibling product or just a variant change
    const newProductId = e.detail?.data?.newProduct?.id 
                      ?? e.detail?.productId 
                      ?? e.detail?.product?.id
                      ?? e.detail?.variant?.product_id;
    const currentProductId = this.#productId?.toString();
    
    // Update product ID if changed
    if (newProductId && newProductId.toString() !== currentProductId) {
      this.dataset.productId = newProductId.toString();
    }

    // Attempt to grab the new HTML from the event
    const htmlFrag = e.detail?.data?.html;
    if (htmlFrag) {
      const newComponent = htmlFrag.querySelector('eagleray-mobile-buy-bar');
      if (newComponent) {
        if (newComponent.dataset.productId && newComponent.dataset.productId !== this.dataset.productId) {
          this.dataset.productId = newComponent.dataset.productId;
        }
        // We use morph to replace children. We do NOT replace the root element itself, because we want to preserve state (like is-drawer-open) and our component instance.
        
        // Preserve state classes
        const wasOpen = this.classList.contains('is-drawer-open');
        const hasSize = this.classList.contains('has-size');
        
        morph(this, newComponent, { childrenOnly: true });
        
        if (wasOpen) this.classList.add('is-drawer-open');
        if (hasSize) this.classList.add('has-size');
        
        // Morph removes event listeners on internal elements if they were re-created.
        // But since we use declarative on:click bindings in eagleray-mobile-buy-bar.liquid, we might need to re-bind them or re-cache our refs!
        
        // Actually, the Component base class might re-bind declarative listeners on updatedCallback(), but morph might not trigger it unless we dispatch something, or maybe it does? 
        // Just in case, let's call #cacheInternalElements() to re-cache our manual event listeners (like on sizes).
        this.#cacheInternalElements();
      }
    }

    // Always update the bar logic based on new selected variant
    if (this.#isOurVariantEvent(e) || (newProductId && newProductId.toString() !== currentProductId)) {
      if (newProductId && newProductId.toString() !== currentProductId) {
        // Full sibling reinit (if needed, but morph handled the DOM)
        // We still need to re-bind the picker listener
        this.#reinitForSibling();
      } else {
        this.#updateBar();
      }
    }
  };

  #onCartUpdate = (e) => {
    const realBtn = this.#realAtcButton();
    if (realBtn) realBtn.dataset.puppet = 'false';
    
    const didError = e?.detail?.data?.didError;
    if (this.#pendingBuyNow && !didError) {
      this.#pendingBuyNow = false;
      window.location.href = '/checkout';
      return;
    }
    
    if (this.refs.atcBtn && !didError) {
      setTimeout(() => {
        this.refs.atcBtn?.classList.remove('is-added');
      }, 1600);
    } else if (this.refs.atcBtn) {
      this.refs.atcBtn.classList.remove('is-added');
    }
  };

  #onCartError = () => {
    const realBtn = this.#realAtcButton();
    if (realBtn) realBtn.dataset.puppet = 'false';
    
    this.#pendingBuyNow = false;
    if (this.refs.atcBtn) {
      this.refs.atcBtn.classList.remove('is-added');
    }
  };
}

if (!customElements.get('eagleray-mobile-buy-bar')) {
  customElements.define('eagleray-mobile-buy-bar', MobileBuyBarComponent);
}
